import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Search, User, Building2, ShieldCheck, ChevronRight, Users as UsersIcon } from "lucide-react";
import { EmptyState } from "../EmptyState";

const ROLE_LABEL = { candidate: "Candidato", company: "Empresa", admin: "Admin" };
const ROLE_CLS = {
  candidate: "bg-sky-100 text-sky-700",
  company: "bg-amber-100 text-amber-700",
  admin: "bg-red-100 text-red-700",
};
const ROLE_ICON = { candidate: User, company: Building2, admin: ShieldCheck };

export function AdminUsersSection({ onOpenUser }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [active, setActive] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (role) params.set("role", role);
      if (active) params.set("active", active);
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setRows(data.users);
      setTotal(data.total);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [search, role, active, offset]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => { setOffset(0); }, [search, role, active]);

  return (
    <div className="space-y-5">
      <div>
        <h2 data-testid="admin-users-title" className="font-heading font-black tracking-tighter text-2xl text-slate-900">Utilizadores</h2>
        <p className="text-sm text-slate-500">Lista completa de contas. Filtra por role e estado, ou clica numa linha para o perfil.</p>
      </div>

      <div className="bg-white rounded-xl border border-black/5 p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="admin-users-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Procurar por email, nome ou telemóvel..."
            className="pl-9"
          />
        </div>
        <select
          data-testid="admin-users-filter-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm text-slate-700"
        >
          <option value="">Todos os roles</option>
          <option value="candidate">Candidatos</option>
          <option value="company">Empresas</option>
          <option value="admin">Admins</option>
        </select>
        <select
          data-testid="admin-users-filter-active"
          value={active}
          onChange={(e) => setActive(e.target.value)}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm text-slate-700"
        >
          <option value="">Todos os estados</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos / anonimizados</option>
        </select>
        <div className="text-xs text-slate-500 tabular-nums">
          {loading ? "A carregar..." : `${total} utilizadores`}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10"><EmptyState icon={UsersIcon} title="Nenhum utilizador" description="Ajusta os filtros ou a pesquisa." /></div>
        ) : (
          <ul className="divide-y divide-black/5">
            {rows.map((u) => {
              const RoleIcon = ROLE_ICON[u.role] || User;
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    data-testid={`admin-user-row-${u.id}`}
                    onClick={() => onOpenUser(u.id)}
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className={`w-10 h-10 rounded-full ${ROLE_CLS[u.role] || "bg-slate-100"} flex items-center justify-center shrink-0`}>
                      <RoleIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-slate-900 truncate">{u.name || "(sem nome)"}</p>
                        <Badge className={`${ROLE_CLS[u.role] || "bg-slate-100 text-slate-600"} hover:${ROLE_CLS[u.role] || ""} text-[10px]`}>
                          {ROLE_LABEL[u.role] || u.role}
                        </Badge>
                        {!u.active && <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200 text-[10px]">Inativo</Badge>}
                        {u.premium_until && new Date(u.premium_until) > new Date() && (
                          <Badge className="bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-100 text-[10px]">Premium</Badge>
                        )}
                        {u._demo && <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100 text-[10px]">demo</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}{u.district ? ` · ${u.district}` : ""}</p>
                    </div>
                    <span className="text-[11px] text-slate-400 hidden sm:block">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("pt-PT") : ""}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {total > limit && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">{offset + 1}–{Math.min(offset + limit, total)} de {total}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Anterior</Button>
            <Button size="sm" variant="outline" className="rounded-full" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>Seguinte</Button>
          </div>
        </div>
      )}
    </div>
  );
}
